import { useState } from "react";
import { Download } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * M9: One-shot CSV download button that re-uses the current admin filters.
 *
 * `path`   — e.g. "/admin/customers/export.csv"
 * `params` — object of query params (omit empties)
 * `filename` — final saved filename
 * `label`  — button label (defaults to "Export CSV")
 * `testId` — data-testid for the button
 */
export default function ExportCSVButton({ path, params = {}, filename, label = "Export CSV", testId = "export-csv-btn" }) {
  const [busy, setBusy] = useState(false);

  const onClick = async () => {
    setBusy(true);
    try {
      const r = await api.get(path, { params, responseType: "blob" });
      const blob = new Blob([r.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || "colourmatch-export.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (e) {
      const code = e?.response?.status;
      if (code === 413) toast.error("Too many rows — narrow the date range");
      else if (code === 403) toast.error("Access denied for this export");
      else toast.error(`Export failed${code ? ` (${code})` : ""}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      onClick={onClick}
      disabled={busy}
      data-testid={testId}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border border-brand-linen bg-white hover:bg-brand-linen/40 disabled:opacity-50"
    >
      <Download className="w-3.5 h-3.5" />
      {busy ? "Exporting…" : label}
    </button>
  );
}
